import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { EditableSelect } from '@/components/EditableSelect';
import { Field, Input, Select, Textarea } from '@/components/Form';
import { productionPlansApi } from '@/api/production-plans';
import { extractApiError } from '@/api/client';
import { modelsApi } from '@/api/models';
import { useToast } from '@/components/Toast';
// 추가수정 2차(26-07-27) 정정 — '오늘'(KST)은 공용 포맷터 하나만 쓴다.
import { kstTodayYmd } from '@/lib/format-date';

// docs/work_log/v1203-100-percent §1 — ProductionPlan 신규 생성 모달.
// 기존엔 backend API 직접 호출만 가능 — UI 추가.
//
// Round 2 Phase F (#8) — 로컬 Field(필드 전체를 <label>로 감싸는 안티패턴)와
// box-sizing 없는 로컬 input 을 공유 Form 컴포넌트(Field/Input/Select/Textarea)
// 로 교체. 라벨은 htmlFor 로만 연결돼 빈 영역·EditableSelect 의 옵션 관리 버튼
// 클릭을 더는 가로채지 않는다. 수량·우선순위는 편집 중 raw string state 로
// 두고 blur 에서만 clamp 한다(빈칸·다자리 입력 가능 — 기존엔 매 키 입력마다
// Number()||1 로 clamp 돼 1 로 튕겼다).

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ProductionPlanCreateModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const [modelId, setModelId] = useState('');
  // Round 2 Phase F (#8) — 편집 중 raw string. 파생 숫자(plannedCount/priority)는
  // 제출·검증에서 사용하고, blur 시에만 범위로 보정한다.
  const [plannedCountStr, setPlannedCountStr] = useState('1');
  const [priorityStr, setPriorityStr] = useState('0');
  const [estimatedCompletionAt, setEstimatedCompletionAt] = useState(() => {
    // default: 30일 후.
    // 추가수정 2차(26-07-27) 정정 — 기존 `toISOString().slice(0,10)` 은 UTC 기준
    // 이라 한국 새벽 0~9시에 계획을 만들면 기본값이 하루 이른 날짜로 들어갔다.
    // kstTodayYmd 는 now 를 주입받으므로 "30일 후"도 KST 기준으로 뽑힌다.
    return kstTodayYmd(Date.now() + 30 * 24 * 60 * 60_000);
  });
  const [batchCode, setBatchCode] = useState('');
  const [notes, setNotes] = useState('');
  // S2 Phase 2.9 (§9.4) — full 세분화 spec 3축 (model × 색상 × 불감지 × 구분사양).
  const [color, setColor] = useState('');
  const [sensorType, setSensorType] = useState('');
  const [variantValue, setVariantValue] = useState('');

  // 파생 숫자값(검증/제출용). 빈칸이면 0 으로 간주 → onSubmit 의 plannedCount<1 가드.
  const plannedCount = Math.max(0, Math.floor(Number(plannedCountStr) || 0));
  const priority = Math.max(0, Math.floor(Number(priorityStr) || 0));

  const modelsQ = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    enabled: open,
  });

  // FB2 §7A — 선택 모델의 "구분 사양" 축을 알아야 variantValue picker 를
  // 구동할 수 있다. ⚠️ 이 모달은 modelId(=Model.id)를 보유하므로 로봇 폼
  // (code 매칭)과 달리 m.id 로 매칭한다. 축이 없는 모델(variantOptionGroupKey
  // == null, 예: SEN/HEADLESS)은 picker 를 숨긴다.
  const selectedModel = modelsQ.data?.items.find((m) => m.id === modelId);

  const create = useMutation({
    mutationFn: productionPlansApi.create,
    onSuccess: (created) => {
      toast.success(`${created.humanId} 제조 계획을 만들었습니다.`);
      qc.invalidateQueries({ queryKey: ['production-plans'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      reset();
      onClose();
    },
    onError: (err) => {
      // extractApiError handles ProblemDetails (400/422) + AxiosError + Error
      // uniformly — the raw err.response.data.message read returned undefined for
      // ProblemDetails responses, so the user saw the generic '생성 실패' instead
      // of the actual validation message.
      toast.error(extractApiError(err));
    },
  });

  function reset() {
    setModelId('');
    setPlannedCountStr('1');
    setBatchCode('');
    setPriorityStr('0');
    setNotes('');
    setColor('');
    setSensorType('');
    setVariantValue('');
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!modelId) {
      toast.error('모델을 선택해 주세요.');
      return;
    }
    if (plannedCount < 1) {
      toast.error('수량은 1 이상이어야 합니다.');
      return;
    }
    // 09:00 KST 의 ISO 로 변환
    const isoEta = new Date(`${estimatedCompletionAt}T09:00:00.000Z`).toISOString();
    create.mutate({
      modelId,
      plannedCount,
      estimatedCompletionAt: isoEta,
      ...(batchCode ? { batchCode } : {}),
      ...(notes ? { notes } : {}),
      priority,
      // S2 Phase 2.9 (§9.4) — full 세분화 spec 3축 (빈 값은 미설정).
      ...(color ? { color } : {}),
      ...(sensorType ? { sensorType } : {}),
      ...(variantValue.trim() ? { variantValue: variantValue.trim() } : {}),
    });
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!create.isPending) {
          reset();
          onClose();
        }
      }}
      title="새 제조 계획"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={create.isPending}
            style={btn}
          >
            취소
          </button>
          <button
            type="submit"
            form="production-plan-create-form"
            disabled={create.isPending}
            style={{
              ...btn,
              background: 'var(--blue-600)',
              color: 'var(--fg-inverse)',
              fontWeight: 600,
            }}
          >
            {create.isPending ? '생성 중…' : '계획 추가'}
          </button>
        </div>
      }
    >
      <form
        id="production-plan-create-form"
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <Field label="모델" required htmlFor="pp-model">
          <Select
            id="pp-model"
            value={modelId}
            onChange={(e) => {
              setModelId(e.target.value);
              // FB2 §7A — 모델 변경 시 이전 모델의 구분 사양 값이 남지 않도록
              // variantValue 를 리셋한다(축이 모델마다 다름).
              setVariantValue('');
            }}
            required
          >
            <option value="">선택…</option>
            {modelsQ.data?.items?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName} ({m.code})
              </option>
            ))}
          </Select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="계획 수량" required htmlFor="pp-count">
            <Input
              id="pp-count"
              type="number"
              min={1}
              value={plannedCountStr}
              onChange={(e) => setPlannedCountStr(e.target.value)}
              onBlur={() =>
                setPlannedCountStr((s) => String(Math.max(1, Math.floor(Number(s) || 1))))
              }
              required
            />
          </Field>
          <Field label="우선순위" htmlFor="pp-priority">
            <Input
              id="pp-priority"
              type="number"
              min={0}
              value={priorityStr}
              onChange={(e) => setPriorityStr(e.target.value)}
              onBlur={() => setPriorityStr((s) => String(Math.max(0, Math.floor(Number(s) || 0))))}
            />
          </Field>
        </div>

        <Field label="완성 예정일 (ETA)" required htmlFor="pp-eta">
          <Input
            id="pp-eta"
            type="date"
            value={estimatedCompletionAt}
            onChange={(e) => setEstimatedCompletionAt(e.target.value)}
            required
          />
        </Field>

        {/* S2 Phase 2.9 (§9.4) — full 세분화 spec 3축. 같은 모델도 색상/불감지/
            구분 사양별 수량이 다르므로 plan 단위로 입력. materialize 시 생산
            로봇이 이 spec 을 상속하고, 배정 인박스가 계약 희망 spec 과 매칭. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="색상" hint="비워 둬도 됩니다." htmlFor="pp-color">
            <EditableSelect
              id="pp-color"
              groupKey="robot.color"
              value={color}
              onChange={setColor}
              placeholder="— 선택 —"
            />
          </Field>
          <Field label="불감지" hint="F 또는 N. 비워 둬도 됩니다." htmlFor="pp-sensor">
            <EditableSelect
              id="pp-sensor"
              groupKey="robot.sensorType"
              value={sensorType}
              onChange={setSensorType}
              placeholder="— 선택 —"
            />
          </Field>
        </div>

        {/* FB2 §7A — 구분 사양을 자유텍스트 → 모델별 picklist 로 교체. 선택 모델의
            variantOptionGroupKey 로 EditableSelect 를 구동하고, 라벨은 모델의
            variantAxisLabel(없으면 '구분 사양'). 축이 없는 모델
            (variantOptionGroupKey == null, 예: SEN/HEADLESS)은 필드를 숨긴다.
            매니저는 EditableSelect 의 "옵션 추가/삭제…" 로 값을 확장할 수 있다. */}
        {selectedModel?.variantOptionGroupKey && (
          <Field
            label={selectedModel.variantAxisLabel ?? '구분 사양'}
            hint="목록에 없으면 매니저가 '옵션 추가/삭제…'에서 값을 더할 수 있습니다."
            htmlFor="pp-variant"
          >
            <EditableSelect
              id="pp-variant"
              groupKey={selectedModel.variantOptionGroupKey}
              value={variantValue}
              onChange={setVariantValue}
              placeholder="— 선택 —"
            />
          </Field>
        )}

        <Field label="배치 코드 (선택)" hint="예: 2026Q2-B01" htmlFor="pp-batch">
          <Input
            id="pp-batch"
            type="text"
            value={batchCode}
            onChange={(e) => setBatchCode(e.target.value)}
            placeholder="2026Q2-B01"
          />
        </Field>

        <Field label="메모" htmlFor="pp-notes">
          <Textarea
            id="pp-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="추가 정보"
          />
        </Field>
      </form>
    </Modal>
  );
}

const btn: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
  fontSize: 13,
};
